import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createViewingHistoryEntry, initialLocalState, retainSeatDecision, normalizeViewingPlan, preserveReviewSeats, sameSeatVisit, type FitProfile, type LocalReview, type LocalState, type SeatCandidate, type SeatDecisionRecord, type ViewingPlan } from '@anjabom/core';
import { staleDrafts, syncOutbox } from '../lib/outbox';

import { readLocalState, persistLocalState, type RestoreResult } from './local-state-storage';
import { LocalStateRecovery } from './LocalStateRecovery';
import { exportLocalBackup, persistBackupRestore } from './local-state-backup';

interface LocalStateContextValue {
  state: LocalState;
  storageProblem: 'corrupt' | 'read' | 'write' | 'conflict' | null;
  getRestoreToken(): string;
  restoreBackup(state: LocalState, token: string): { ok: boolean; message: string };
  addCandidate(candidate: SeatCandidate): boolean;
  retrySave(): boolean;
  removeCandidate(candidateId: string): void;
  clearCandidates(): void;
  replaceCandidates(candidates: SeatCandidate[]): void;
  addReview(review: LocalReview): boolean;
  updateFitProfile(profile: FitProfile): boolean;
  resetAfterDeletion(): boolean;
  rememberVenue(venueId: string): void;
  updateViewingPlan(plan: ViewingPlan): boolean;
  selectViewingPlan(plan: ViewingPlan): boolean;
  removeUpcomingPlan(id: string): boolean;
  removeViewingHistory(id: string): void;
  saveSeatDecision(decision: SeatDecisionRecord): boolean;
  removeSeatDecision(id: string): void;
}

const LocalStateContext = createContext<LocalStateContextValue | null>(null);

export function LocalStateProvider({ children }: { children: ReactNode }) {
  const [restored] = useState<RestoreResult>(() => typeof window === 'undefined'
    ? { state: initialLocalState, raw: null, problem: null }
    : readLocalState({ getItem: (key) => window.localStorage.getItem(key), setItem: (key, value) => window.localStorage.setItem(key, value) }));
  const [state, renderState] = useState(restored.state);
  const current = useRef(restored.state);
  const original = useRef(restored.raw);
  const [problem, setProblem] = useState<'corrupt' | 'read' | 'write' | 'conflict' | null>(restored.problem);
  const [restoreNotice, setRestoreNotice] = useState('');
  const blocked = useRef(restored.problem !== null);
  const storage = useMemo(() => ({ getItem: (key: string) => window.localStorage.getItem(key), setItem: (key: string, value: string) => window.localStorage.setItem(key, value) }), []);
  const persist = useCallback((next: LocalState) => {
    const result = persistLocalState(storage, next, original.current);
    if (result.ok) { original.current = result.raw; setProblem(null); }
    else setProblem(result.problem);
    return result.ok;
  }, [storage]);
  const setState = useCallback((update: (previous: LocalState) => LocalState): boolean => {
    if (blocked.current) return false;
    const next = update(current.current);
    current.current = next;
    renderState(next);
    return persist(next);
  }, [persist]);
  const retrySave = useCallback(() => !blocked.current && persist(current.current), [persist]);
  const retryRead = () => {
    const next = readLocalState(storage);
    if (next.problem) { setProblem(next.problem); return; }
    original.current = next.raw;
    current.current = next.state;
    blocked.current = false;
    renderState(next.state);
    setProblem(null);
  };
  const reset = () => {
    if (!window.confirm('이 기기의 좌석 후보·관람 계획·좌석 결정을 초기화할까요? 내려받거나 복사하지 않은 내용은 되돌릴 수 없어요.')) return;
    // Re-read before the explicit reset. A failed read never authorizes a write.
    const fresh = readLocalState(storage);
    if (fresh.problem === 'read') { setProblem('read'); return; }
    const result = persistLocalState(storage, initialLocalState, fresh.raw);
    if (!result.ok) { setProblem(result.problem); return; }
    original.current = result.raw;
    current.current = initialLocalState;
    blocked.current = false;
    renderState(initialLocalState);
    setProblem(null);
  };
  const getRestoreToken = useCallback(() => JSON.stringify([original.current, current.current]), []);
  const restoreBackup = useCallback((next: LocalState, token: string) => {
    if (token !== getRestoreToken()) return { ok: false, message: '미리보기 뒤 현재 화면의 내용이 바뀌었어요. 파일을 다시 골라 비교해 주세요.' };
    const result = persistBackupRestore(storage, next, original.current);
    if (!result.ok) return result;
    original.current = result.raw;
    current.current = next;
    blocked.current = false;
    renderState(next);
    setProblem(null);
    setRestoreNotice('백업을 적용하고 저장된 내용을 다시 확인했어요. 설정의 백업 화면에서 복원 전 원본도 내려받을 수 있어요.');
    return { ok: true, message: '백업을 적용하고 저장된 내용을 다시 확인했어요.' };
  }, [getRestoreToken, storage]);
  useEffect(()=>{const maintain=()=>{void syncOutbox().catch(() => undefined);void staleDrafts().then((rows)=>{if(rows.length&&window.confirm(`90일이 지난 미전송 초안 ${rows.length}개가 있어요. 설정에서 확인해 주세요.`))window.location.assign('/settings');}).catch(() => undefined);};window.addEventListener('online',maintain);document.addEventListener('visibilitychange',maintain);maintain();return()=>{window.removeEventListener('online',maintain);document.removeEventListener('visibilitychange',maintain);};},[]);

  const addCandidate = useCallback((candidate: SeatCandidate) => {
    if (current.current.candidates.length >= 4 || current.current.candidates.some((seat) => sameSeatVisit(seat, candidate))) return false;
    return setState((current) => {
      return { ...current, candidates: [...current.candidates, candidate] };
    });
  }, [setState]);
  const removeCandidate = useCallback((candidateId: string) => {
    setState((current) => ({ ...preserveReviewSeats(current), candidates: current.candidates.filter((item) => item.candidateId !== candidateId) }));
  }, [setState]);
  const clearCandidates = useCallback(() => {
    setState((current) => ({ ...preserveReviewSeats(current), candidates: [] }));
  }, [setState]);
  const replaceCandidates = useCallback((candidates: SeatCandidate[]) => {
    setState((current) => ({ ...preserveReviewSeats(current), candidates: candidates.slice(0, 4) }));
  }, [setState]);
  const addReview = useCallback((review: LocalReview) => {
    return setState((current) => preserveReviewSeats({
      ...current,
      reviews: [review, ...current.reviews.filter((item) => item.id !== review.id)],
    }));
  }, [setState]);
  const updateFitProfile = useCallback((fitProfile: FitProfile) => {
    return setState((current) => ({ ...current, fitProfile }));
  }, [setState]);
  const rememberVenue = useCallback((venueId: string) => {
    setState((current) => ({
      ...current,
      recentVenueIds: [venueId, ...current.recentVenueIds.filter((id) => id !== venueId)].slice(0, 5),
    }));
  }, [setState]);
  const selectViewingPlan = useCallback((plan:ViewingPlan)=>setState((current)=>({...current,viewingPlan:normalizeViewingPlan(plan)})),[setState]);
  const removeUpcomingPlan = useCallback((id:string)=>setState((current)=>({...current, viewingPlan:(current.viewingPlan.id??`${current.viewingPlan.eventTitle}:${current.viewingPlan.startsAt}`)===id?initialLocalState.viewingPlan:current.viewingPlan, upcomingPlans:current.upcomingPlans.filter((p)=>(p.id??`${p.eventTitle}:${p.startsAt}`)!==id)})),[setState]);
  const updateViewingPlan = useCallback((viewingPlan: ViewingPlan) => {
    const normalized = normalizeViewingPlan(viewingPlan);
    if (!normalized.id) normalized.id = crypto.randomUUID();
    if (current.current.upcomingPlans.length >= 12 && !current.current.upcomingPlans.some((p)=>p.id===normalized.id)) return false;
    return setState((current) => {
      if (!normalized.eventTitle.trim()) return { ...current, viewingPlan: normalized };
      const entry = createViewingHistoryEntry(normalized, normalized.updatedAt ?? new Date().toISOString());
      const sameVisit = (candidate: LocalState['viewingPlanHistory'][number]) => candidate.eventTitle === entry.eventTitle && candidate.startsAt === entry.startsAt;
      return { ...current, viewingPlan: normalized, upcomingPlans: [normalized, ...current.upcomingPlans.filter((p)=>p.id ? p.id !== normalized.id : !(p.eventTitle === normalized.eventTitle && p.startsAt === normalized.startsAt))].sort((a,b)=>a.startsAt.localeCompare(b.startsAt)), viewingPlanHistory: [entry, ...current.viewingPlanHistory.filter((candidate) => !sameVisit(candidate))].slice(0, 24) };
    });
  }, [setState]);
  const removeViewingHistory = useCallback((id: string) => {
    setState((current) => ({ ...current, viewingPlanHistory: current.viewingPlanHistory.filter((entry) => entry.id !== id) }));
  }, [setState]);
  const saveSeatDecision = useCallback((decision: SeatDecisionRecord) => {
    const seatDecisions = retainSeatDecision(current.current.seatDecisions, decision);
    if (!seatDecisions) return false;
    return setState((current) => ({
      ...current,
      seatDecisions,
    }));
  }, [setState]);
  const removeSeatDecision = useCallback((id: string) => {
    setState((current) => ({ ...current, seatDecisions: current.seatDecisions.filter((entry) => entry.id !== id) }));
  }, [setState]);

  const resetAfterDeletion = useCallback(() => {
    try { if (storage.getItem('anjabom.local-state.v1') !== null) return false; } catch { return false; }
    original.current = null; current.current = initialLocalState; blocked.current = false;
    renderState(initialLocalState); setProblem(null); setRestoreNotice('');
    return true;
  }, [storage]);
  const value = useMemo(() => ({ state, storageProblem: problem, getRestoreToken, restoreBackup, addCandidate, retrySave, removeCandidate, clearCandidates, replaceCandidates, addReview, updateFitProfile, resetAfterDeletion, rememberVenue, selectViewingPlan, removeUpcomingPlan, updateViewingPlan, removeViewingHistory, saveSeatDecision, removeSeatDecision }), [
    state, problem, getRestoreToken, restoreBackup, addCandidate, retrySave, removeCandidate, clearCandidates, replaceCandidates, addReview, updateFitProfile, resetAfterDeletion, rememberVenue, selectViewingPlan, removeUpcomingPlan, updateViewingPlan, removeViewingHistory, saveSeatDecision, removeSeatDecision,
  ]);
  return <LocalStateContext.Provider value={value}>
    {restoreNotice ? <p className="card local-state-recovery" role="status">{restoreNotice}</p> : null}
    {problem ? <LocalStateRecovery problem={problem} original={original.current} pending={blocked.current ? null : exportLocalBackup(state)} onRetry={() => blocked.current ? retryRead() : persist(current.current)} onReload={retryRead} onReset={reset} /> : null}
    {!blocked.current ? children : null}
  </LocalStateContext.Provider>;
}

export function useLocalState(): LocalStateContextValue {
  const value = useContext(LocalStateContext);
  if (!value) throw new Error('LocalStateProvider is missing');
  return value;
}
